import { useEffect, useRef, useState, type ReactNode } from 'react';
import { stationState, type StationState } from '@lw/shared/indicator';
import type { ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import { fmtMMSS, fmtTime, MILESTONES, milestoneIndex } from '../app/helpers.js';

/** §7.3 indicator pill with popover */
export function IndicatorPill(): ReactNode {
  const { rt } = useRuntime();
  const [open, setOpen] = useState(false);
  const ind = rt.client.indicator.current();
  const lbl = rt.client.indicator.label();
  const cls = ind.colour === 'GREEN' ? 'green' : ind.colour === 'ORANGE' ? 'orange' : 'red';
  return (
    <>
      <button className={`pill ${cls}`} onClick={() => setOpen((o) => !o)} role="status" aria-live={cls === 'red' ? 'assertive' : 'polite'} aria-label={`Connection ${lbl.label} ${lbl.value}`} data-testid="pill">
        <span className="glyph">{lbl.glyph}</span>
        <span>{lbl.label}</span>
        {lbl.value ? <span className="val">{lbl.value}</span> : null}
      </button>
      {open ? <Popover onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function Popover({ onClose }: { onClose: () => void }): ReactNode {
  const { rt } = useRuntime();
  const c = rt.client;
  const m = c.chrono.metrics();
  const u = c.chrono.uncertainty();
  const lbl = c.indicator.label();
  const peerRole = rt.id.role === 'UAV' ? 'FDC' : rt.id.role === 'FDC' ? 'UAV' : null;
  const peer = peerRole ? c.stations[peerRole] : undefined;
  const now = c.chrono.serverNow();
  return (
    <div className="popover" role="dialog" aria-label="Connection details" onClick={onClose}>
      <h3>Connection &amp; time sync</h3>
      <table>
        <tbody>
          <tr><td>State</td><td>{lbl.label} {lbl.value}</td></tr>
          <tr><td>Own sync uncertainty</td><td>{u === null ? '—' : `${u.toFixed(0)} ms`}</td></tr>
          <tr><td>Best / p90 RTT</td><td>{c.chrono.rttBest() === null ? '—' : `${(c.chrono.rttBest() as number).toFixed(0)} / ${m.rttP90_30?.toFixed(0) ?? '—'} ms`}</td></tr>
          <tr><td>Loss (30 s)</td><td>{`${Math.round(m.loss30 * 100)} %`}</td></tr>
          <tr><td>Last sample</td><td>{m.sincePongMs === null ? '—' : `${fmtMMSS(m.sincePongMs)} ago`}</td></tr>
          {peerRole ? (
            <>
              <tr><td>{peerRole} sync uncertainty</td><td>{peer && !peer.lost ? `${Math.round(peer.u)} ms` : 'unknown'}</td></tr>
              <tr><td>Pair bound (FDC↔UAV)</td><td>{c.pair?.known ? `${c.pair.bound} ms` : 'unknown → PEER?'}</td></tr>
            </>
          ) : null}
          <tr><td>Mode</td><td>{c.chrono.currentMode()}</td></tr>
          <tr><td>Server time</td><td>{fmtTime(now, true)}</td></tr>
        </tbody>
      </table>
      <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>Green only when the FDC and UAV displays are guaranteed to agree within 1 s.</p>
    </div>
  );
}

/** §7.3 stations strip: the other stations, never the Observer for UAV/FDC (C-4). */
export function StationsStrip(): ReactNode {
  const { rt } = useRuntime();
  const role = rt.id.role;
  if (role === 'ADMIN') return null;
  const seen: ('UAV' | 'FDC')[] = role === 'UAV' ? ['FDC'] : role === 'FDC' ? ['UAV'] : ['UAV', 'FDC'];
  const fresh = rt.client.peerFresh();
  return (
    <div className="stations" aria-label="Other stations">
      <span className="lbl">Stations</span>
      {seen.map((r) => {
        // no fresh sync.quality (our own link down, or the tab was backgrounded) → the peer is unknown,
        // never its last-seen value
        const st: { state: StationState; detail: string } = fresh ? stationState(rt.client.stations[r]) : { state: 'STALE', detail: 'no data' };
        const cls = st.state === 'LIVE' ? 'green' : st.state === 'LOST' ? 'red' : 'orange';
        const glyph = st.state === 'LIVE' ? '●' : st.state === 'LOST' ? '⊗' : '▲';
        return (
          <span key={r} className={`pill ${cls}`} data-testid={`station-${r}`}>
            <span className="who">{r}</span>
            <span className="glyph">{glyph}</span>
            <span>{st.state === 'LIVE' ? 'LIVE' : st.state === 'INTERMITTENT' ? 'INTERMITTENT' : st.state === 'STALE' ? 'STALE' : 'LOST'}</span>
            {st.detail ? <span className="val">{st.detail}</span> : null}
          </span>
        );
      })}
    </div>
  );
}

/** §11.3 MilestoneBar — pure from the snapshot and serverNow (the fill is updated by the countdown loop). */
export function MilestoneBar({ snapshot }: { snapshot: ClientSnapshot }): ReactNode {
  const { rt } = useRuntime();
  const now = rt.client.chrono.serverNow();
  const mi = milestoneIndex(snapshot, now);
  if (mi < 0) return null;
  const st = snapshot.state;
  return (
    <nav className="ladder" aria-label="Mission milestones">
      {MILESTONES.map((m, i) => {
        let cls = i < mi ? 'past' : i === mi ? (st === 'FIRE_REVIEW' || (st === 'IN_FLIGHT' && i >= 7) ? 'critical' : 'current') : '';
        if (st === 'AWAITING_DISPOSITION' && i === 10 && !snapshot.uav.laserOffAt) cls = 'current';
        return (
          <div key={m.k} className={`ms ${cls}`}>
            <span className="nm">{m.nm}</span>
            <span className="own">{m.own}</span>
            {i === 7 && st === 'IN_FLIGHT' ? <span className="fill" id="ladderFill" /> : null}
          </div>
        );
      })}
    </nav>
  );
}

/** Observer "NO FIRE MISSION", IMPACT, CEASE LASING full screens */
export function Fullscreen({ snapshot, children }: { snapshot: ClientSnapshot | null; children?: ReactNode }): ReactNode {
  void snapshot;
  return <div className="fullscreen">{children}</div>;
}

/** Red flash at 2 Hz in the laser-warn window (UAV), state-derived per frame (§11.4). */
export function FlashOverlay(): ReactNode {
  const { rt } = useRuntime();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const loop = (): void => {
      const s = rt.client.snapshot;
      const now = rt.client.chrono.serverNow();
      const el = ref.current;
      if (el) {
        const tl = s?.timeline;
        const on = rt.id.role === 'UAV' && s?.state === 'IN_FLIGHT' && tl && now !== null && now >= tl.tLaserWarn && now < tl.tLaserOn && Math.floor(now / 250) % 2 === 0;
        el.classList.toggle('on', !!on);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [rt]);
  return <div className="flash" ref={ref} aria-hidden="true" />;
}

export function ArmGate(): ReactNode {
  const { rt, state } = useRuntime();
  const s = state.snapshot;
  const [, force] = useState(0);
  const needs = rt.id.role !== 'ADMIN' && s && !rt.audio.armed && (s.state === 'IN_FLIGHT' || s.state === 'UAV_READY' || s.state === 'WEAPON_READY' || s.state === 'FIRE_REVIEW');
  if (!needs) return null;
  return (
    <div className="armgate" data-testid="armgate" role="button" tabIndex={0} onPointerDown={() => void rt.audio.arm().then(() => force((n) => n + 1))} onClick={() => void rt.audio.arm().then(() => force((n) => n + 1))} onKeyDown={() => void rt.audio.arm().then(() => force((n) => n + 1))}>
      <div className="box">
        <h2>Tap anywhere to arm audio</h2>
        <p>Warning beeps and callouts need one tap after a reload. The countdown keeps running underneath.</p>
      </div>
    </div>
  );
}
